/**
 * Строит простой объект фильтра категорий для API-запроса.
 * @param {Array} filterCategoryItems — выбранные пункты [{ value, label }]
 * @param {Array} filterCategoryTypes — все опции категорий [{ id, type_id, name }]
 * @param {number} customTypeId — CUSTOM_SPEND или CUSTOM_INCOME
 * @returns {{ regularTypeIds: number[], customTypeIds: number[] } | null}
 *   null — фильтр не нужен (ничего не выбрано)
 */
export function buildCategoryFilterForQuery(
  filterCategoryItems,
  filterCategoryTypes,
  customTypeId
) {
  const selected = getSelectedCategoryOptions(filterCategoryItems, filterCategoryTypes);
  if (!selected.length) return null;

  const regularTypeIds = [];
  const customTypeIds = [];

  selected.forEach((opt) => {
    const parsedTypeId =
      opt?.type_id !== null && opt?.type_id !== undefined ? Number(opt.type_id) : null;
    if (opt.id == customTypeId && parsedTypeId !== null && !Number.isNaN(parsedTypeId)) {
      customTypeIds.push(parsedTypeId);
    } else if (opt.id != null) {
      regularTypeIds.push(Number(opt.id));
    }
  });

  return { regularTypeIds, customTypeIds };
}

/**
 * Находит опции категорий, соответствующие выбранным пунктам фильтра (по value/label/id).
 * @param {Array} filterCategoryItems — выбранные пункты [{ value, label } или { id, type_id, name }]
 * @param {Array} filterCategoryTypes — все опции категорий [{ id, type_id, name }]
 * @returns {Array} опции категорий, которые выбраны
 */
export function getSelectedCategoryOptions(filterCategoryItems, filterCategoryTypes) {
  if (!filterCategoryItems?.length || !filterCategoryTypes?.length) return [];
  return filterCategoryItems
    .map((item) => {
      if (!item) return null;

      // Если элемент уже содержит структуру категории из options — используем как есть.
      if (item?.id != null && item?.type_id != null) {
        return { id: item.id, type_id: item.type_id, name: item.name };
      }

      // Явно пустой/битый элемент выбора игнорируем.
      if (
        item?.id == null &&
        item?.type_id == null &&
        item?.value == null &&
        !item?.label &&
        !item?.name
      ) {
        return null;
      }

      // Для кастомных категорий `id` одинаковый (CUSTOM_*), поэтому
      // сначала пытаемся матчить по названию выбранного пункта.
      const itemLabel = item.label ?? item.name;
      if (itemLabel) {
        const byLabel = filterCategoryTypes.find((o) => o.name === itemLabel);
        if (byLabel) return byLabel;
      }

      // 3) Фолбэк для value/id из localStorage (часто строки).
      // Сначала ищем по id (безопасно для обычных категорий),
      // затем по type_id как запасной вариант.
      const byId =
        filterCategoryTypes.find((o) => o.id == item.value) ??
        filterCategoryTypes.find((o) => o.id == item.id);
      if (byId) return byId;

      const opt =
        filterCategoryTypes.find((o) => o.type_id == item.value) ??
        filterCategoryTypes.find((o) => o.type_id == item.id);
      return opt;
    })
    .filter(Boolean);
}

/**
 * Проверяет, относится ли подкатегория к одной из выбранных категорий.
 * @param {Object} subcategory — подкатегория с base_type_id, custom_type_id
 * @param {Array} selectedCategoryOptions — выбранные опции категорий [{ id, type_id }]
 * @param {number} customTypeId — константа кастомной категории (CUSTOM_INCOME / CUSTOM_SPEND)
 */
export function subcategoryBelongsToSelectedCategories(
  subcategory,
  selectedCategoryOptions,
  customTypeId
) {
  if (!selectedCategoryOptions?.length) return true;
  return selectedCategoryOptions.some((opt) => {
    if (opt.id == null && opt.type_id == null) return false;
    const parsedTypeId =
      opt?.type_id !== null && opt?.type_id !== undefined ? Number(opt.type_id) : null;
    if (opt.id == customTypeId && parsedTypeId !== null && !Number.isNaN(parsedTypeId)) {
      return subcategory.custom_type_id == parsedTypeId;
    }
    // Для системных категорий подкатегория связана по base_type_id именно с id категории.
    return subcategory.base_type_id == opt.id;
  });
}

/**
 * Фильтрует список подкатегорий: только те, что относятся к выбранным категориям.
 * Если категории не выбраны — возвращает все подкатегории.
 */
export function filterSubcategoriesBySelectedCategories(
  subcategories,
  filterCategoryItems,
  filterCategoryTypes,
  customTypeId
) {
  if (!Array.isArray(subcategories)) return [];
  const selected = getSelectedCategoryOptions(filterCategoryItems, filterCategoryTypes);
  if (!selected.length) {
    // Если категории выбраны, но не удалось сматчить ни одну,
    // безопаснее показать пустой список, чем все подкатегории.
    return filterCategoryItems?.length ? [] : subcategories;
  }
  return subcategories.filter((sc) =>
    subcategoryBelongsToSelectedCategories(sc, selected, customTypeId)
  );
}

/**
 * Оставляет в выбранных подкатегориях только те, что относятся к выбранным категориям.
 * Возвращает отфильтрованный массив для setFilterSubcategoryItems.
 */
export function filterSelectedSubcategoriesToCategories(
  filterSubcategoryItems,
  subcategories,
  filterCategoryItems,
  filterCategoryTypes,
  customTypeId
) {
  if (!filterSubcategoryItems?.length) return [];
  const allowedSubcategories = filterSubcategoriesBySelectedCategories(
    subcategories,
    filterCategoryItems,
    filterCategoryTypes,
    customTypeId
  );
  const allowedIds = new Set(allowedSubcategories.map((s) => String(s.id)));
  return filterSubcategoryItems.filter((item) => {
    const id = item.value !== undefined ? item.value : item.id;
    return allowedIds.has(String(id));
  });
}
